import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "@/stores/workspaceStore";
import { useIntegrationConnect } from "./useIntegrationConnect";

type IntegrationConnectContextValue = {
  openConnect: (integrationType: string) => void;
  /** True while the post-click flow is in progress for `integrationType`. */
  connectingFor: string | null;
};

const Ctx = createContext<IntegrationConnectContextValue | null>(null);

export function IntegrationConnectProvider({ children }: { children: ReactNode }) {
  const workspaceId = useAtomValue(workspaceIdAtom);
  const connect = useIntegrationConnect({ workspaceId: workspaceId ?? "" });

  // useIntegrationConnect.openConnect is a fresh arrow function on every
  // render. Route it through a ref so context consumers (every tool
  // message in chat) don't re-render whenever the provider re-renders.
  const openConnectRef = useRef(connect.openConnect);
  openConnectRef.current = connect.openConnect;
  const openConnect = useCallback(
    (integrationType: string) => openConnectRef.current(integrationType),
    [],
  );

  const value = useMemo(
    () => ({ openConnect, connectingFor: connect.connectingFor }),
    [openConnect, connect.connectingFor],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {connect.DialogPortal}
    </Ctx.Provider>
  );
}

/**
 * Returns the connect context if a provider is mounted, or null otherwise.
 * The chat tool-message renderer is mounted on every chat surface, including
 * onboarding routes that don't sit under the Screenata layout, so consumers
 * must handle the missing-provider case (typically by hiding the button).
 */
export function useIntegrationConnectContext(): IntegrationConnectContextValue | null {
  return useContext(Ctx);
}
